import React, { useState } from "react";
import { Link } from "react-router";
import { FiLoader } from "react-icons/fi";
import { apiFetch } from "../utils/apiFetch";
import { emailRegex, passwordRegex } from "../utils/validation";

export default function PasswordRecovery({ reset = false }) {
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get("token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const validToken = /^[a-f0-9]{64}$/.test(token);
  const submit = async event => {
    event.preventDefault();
    if (busy) return;
    setError("");
    if (reset && (!passwordRegex.test(password) || password !== confirm || new TextEncoder().encode(password).length > 72)) {
      setError("Use matching passwords that meet the requirements below (maximum 72 bytes)."); return;
    }
    setBusy(true);
    try {
      const response = await apiFetch(`${process.env.API_URL}/api/auth/${reset ? "reset-password" : "forgot-password"}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(reset ? { token, newPassword: password, confirmPassword: confirm } : { email: email.trim() }),
      });
      const data = await response.json();
      if (!response.ok) throw Error(data.message || "Please try again later.");
      setMessage(data.message); setPassword(""); setConfirm("");
      if (reset) {
        window.history.replaceState(window.history.state, "", window.location.pathname);
        window.dispatchEvent(new Event("auth:expired"));
      }
    } catch (error) { setError(error.message); }
    finally { setBusy(false); }
  };
  const inputClass = "mt-2 min-h-11 w-full rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] px-3 text-[var(--app-text)] focus:outline-2 focus:outline-offset-2 focus:outline-current";
  return <main className="flex min-h-dvh items-center justify-center bg-[var(--app-surface)] px-6 py-10 text-[var(--app-text)]">
    <section className="auth-card w-full max-w-md rounded-lg border border-[var(--app-border)] bg-[var(--app-surface)] p-8 shadow-xl">
      <Link to="/" className="text-xl font-bold">Qronus</Link>
      <h1 className="mt-6 text-2xl font-semibold">{reset ? "Reset password" : "Forgot password?"}</h1>
      <p className="mt-3 text-sm text-[var(--app-secondary)]">{reset ? "Choose a new password. You’ll be signed out on all devices." : "Enter your account email and we’ll send you a link to reset your password."}</p>
      {message ? <p role="status" className="mt-6 text-sm">{message}</p> : reset && !validToken ? <p role="alert" className="mt-6 text-sm text-red-500">This reset link is invalid. Request a new link below.</p> :
        <form onSubmit={submit} className="mt-6 space-y-5">
          {error && <p role="alert" className="text-sm text-red-500">{error}</p>}
          {reset ? <>
            <label className="block text-sm font-medium" htmlFor="new-password">New password<input id="new-password" type="password" autoComplete="new-password" required value={password} onChange={event => setPassword(event.target.value)} className={inputClass} aria-describedby="password-rules" /></label>
            <ul id="password-rules" className="space-y-1 text-xs text-[var(--app-secondary)]">{[
              ["At least 8 characters", password.length >= 8], ["An uppercase letter", /[A-Z]/.test(password)],
              ["A lowercase letter", /[a-z]/.test(password)], ["A number", /\d/.test(password)],
              ["A symbol", /[^a-zA-Z0-9]/.test(password)],
            ].map(([label, met]) => <li key={label} className={met ? "text-green-600 dark:text-green-400" : ""}>{met ? "✓ " : "• "}{label}</li>)}</ul>
            <label className="block text-sm font-medium" htmlFor="confirm-password">Confirm password<input id="confirm-password" type="password" autoComplete="new-password" required value={confirm} onChange={event => setConfirm(event.target.value)} className={inputClass} /></label>
          </> : <label className="block text-sm font-medium" htmlFor="recovery-email">Email address<input id="recovery-email" type="email" autoComplete="email" maxLength={254} required value={email} onChange={event => setEmail(event.target.value)} className={inputClass} /></label>}
          <button disabled={busy || (!reset && !emailRegex.test(email.trim()))} aria-busy={busy} className="flex min-h-11 w-full items-center justify-center gap-2 rounded-md bg-[var(--app-text)] px-4 font-semibold text-[var(--app-surface)] disabled:opacity-50">{busy && <FiLoader className="animate-spin" />}{reset ? "Reset password" : "Send reset link"}</button>
        </form>}
      {reset && !message && <Link to="/forgot-password" className="mt-5 block text-sm underline">Request a new reset link</Link>}
      <Link to="/login" className="mt-6 block text-center text-sm underline">Back to sign in</Link>
    </section>
  </main>;
}
